import { Dictionary } from "lodash";
import React, { createContext } from "react";

import { SERVICES } from "constants/constants";
import { Cutout, CutoutInProgress } from "models/Cutout";
import { LayerCompact, LayerReview } from "models/Layer";
import { Project } from "models/Project";
import { Shot, ShotCompact } from "models/Shot";
import { Subscription, ThrottleUsage, User } from "models/User";

export interface ShotContextType {
  shotId: string | undefined;
  layerId: string | undefined;
  selectedShot: Shot | null;
  setSelectedShot: React.Dispatch<React.SetStateAction<Shot | null>>;
  project: Project;
  setShots: React.Dispatch<React.SetStateAction<Dictionary<ShotCompact[]>>>;
  isSelectedShotLoading: boolean;
  setIsSelectedShotLoading: React.Dispatch<React.SetStateAction<boolean>>;
  layers: LayerCompact[] | undefined;
  setLayers: React.Dispatch<React.SetStateAction<LayerCompact[] | undefined>>;
  cutouts: Cutout[];
  setCutouts: React.Dispatch<React.SetStateAction<Cutout[]>>;
  shotCutouts: Cutout[];
  setShotCutouts: React.Dispatch<React.SetStateAction<Cutout[]>>;
  cutoutInProgress: CutoutInProgress | null;
  setCutoutInProgress: React.Dispatch<
    React.SetStateAction<CutoutInProgress | null>
  >;
  /** Cutout currently focused in the review panel (timeline / cascade). */
  selectedCutout: Cutout | null;
  setSelectedCutout: React.Dispatch<React.SetStateAction<Cutout | null>>;
  selectedLayer: LayerCompact | null;
  setSelectedLayer: React.Dispatch<React.SetStateAction<LayerCompact | null>>;
  reviewLayer: LayerReview | null;
  setReviewLayer: React.Dispatch<React.SetStateAction<LayerReview | null>>;
  imageData: string | null;
  setImageData: React.Dispatch<React.SetStateAction<string | null>>;
  selectedFrame: number | null;
  setSelectedFrame: React.Dispatch<React.SetStateAction<number | null>>;
  showExportPanel: boolean;
  setShowExportPanel: React.Dispatch<React.SetStateAction<boolean>>;
  layersForExport: LayerCompact[];
  setLayersForExport: React.Dispatch<React.SetStateAction<LayerCompact[]>>;
  isExporting: boolean;
  isAnnotating: boolean;
  setIsExporting: React.Dispatch<React.SetStateAction<boolean>>;
  /** True while annotation mask/cover is uploading (Workspace / EditMode). */
  isAnnotationSaving: boolean;
  setIsAnnotationSaving: React.Dispatch<React.SetStateAction<boolean>>;
  clearAnnotationData: () => void;
  service: SERVICES;
  setService: React.Dispatch<React.SetStateAction<SERVICES>>;
  showTrackingPanel: boolean;
  setShowTrackingPanel: React.Dispatch<React.SetStateAction<boolean>>;
  subscription: Subscription | null;
  throttleUsage: ThrottleUsage | null;
  getThrottleUsage: () => void;
  /** Smart Vectors: when set, review panel shows inference preview video */
  smartVectorsReviewPane: "inference_video" | null;
  setSmartVectorsReviewPane: React.Dispatch<
    React.SetStateAction<"inference_video" | null>
  >;
  /** Registered by Shot; same behavior as leaving annotation mode from the workspace (back). */
  exitAnnotationModeRef: React.MutableRefObject<(() => void) | null>;
  /** Registered by Shot; opens Add ROI intro modal or runs annotation immediately. */
  requestSmartVectorsAddRoiRef: React.MutableRefObject<(() => void) | null>;
}

const defaultShotContextValue: ShotContextType = {
  shotId: undefined,
  layerId: undefined,
  selectedShot: null,
  setSelectedShot: () => {},
  project: {} as Project,
  setShots: () => {},
  isSelectedShotLoading: false,
  setIsSelectedShotLoading: () => {},
  layers: undefined,
  setLayers: () => {},
  cutouts: [],
  setCutouts: () => {},
  shotCutouts: [],
  setShotCutouts: () => {},
  cutoutInProgress: null,
  setCutoutInProgress: () => {},
  selectedCutout: null,
  setSelectedCutout: () => {},
  selectedLayer: null,
  setSelectedLayer: () => {},
  reviewLayer: null,
  setReviewLayer: () => {},
  imageData: null,
  setImageData: () => {},
  selectedFrame: null,
  setSelectedFrame: () => {},
  showExportPanel: false,
  setShowExportPanel: () => {},
  layersForExport: [],
  setLayersForExport: () => {},
  isExporting: false,
  isAnnotating: false,
  setIsExporting: () => {},
  isAnnotationSaving: false,
  setIsAnnotationSaving: () => {},
  clearAnnotationData: () => {},
  service: SERVICES.ROTOSCOPING,
  setService: () => {},
  showTrackingPanel: false,
  setShowTrackingPanel: () => {},
  subscription: null,
  throttleUsage: null,
  getThrottleUsage: () => {},
  smartVectorsReviewPane: null,
  setSmartVectorsReviewPane: () => {},
  exitAnnotationModeRef: { current: null },
  requestSmartVectorsAddRoiRef: { current: null },
};

const ShotContext = createContext<ShotContextType>(defaultShotContextValue);

export default ShotContext;
